import { addDays, differenceInDays, isBefore } from 'date-fns';
import React from 'react';
import styled from 'styled-components/native';
import CircularProgress from '../../components/CircularProgressBar';
import { SubCaption, Subtitle } from '../../components/Typography';
import Colors from '../../constants/Colors';
import elementIDs from '../../../elementIDs';

// Containers ------------------------------------------------------------------

const StyledViewContainer = styled.View`
  position: absolute;
`;

// Texts -----------------------------------------------------------------------

const StyledStatusPercentText = styled(Subtitle)<{ textSize?: string }>`
  font-size: ${p => (p.textSize === 'small' ? 18 : 20.17)}px;
  line-height: ${p => (p.textSize === 'small' ? 18 : 20.17)}px;
  letter-spacing: -0.07px;
  text-align: center;
`;

const StyledStatusText = styled(SubCaption)<{ textSize?: string }>`
  font-family: 'WorkSans-Regular';
  font-size: ${p => (p.textSize === 'small' ? 11 : 12)}px;
  line-height: ${p => (p.textSize === 'small' ? 11 : 24)}px;
  letter-spacing: -0.07px;
  text-align: center;
  margin-top: -1.5px;
`;

// Screen Component Props ------------------------------------------------------

export interface StudyDynamicCircleProps {
  statusType: string;
  startDate?: Date | number;
  studyLength: number;
  textSize?: 'small' | 'large';
  textColor?: 'light' | 'medium' | 'dark';
  progressColor?: string;
  progressWidth?: number;
  size?: number;
  display?: 'daysLeft' | 'daysLogged';
  isCompletedStudy?: boolean;
}

// Functions -------------------------------------------------------------------

const percentageCompleted = (studyLength: number, daysCompleted: number) => {
  const value = Math.trunc((daysCompleted / studyLength) * 100);
  return isNaN(value) ? 0 : value;
};

const circleCustomView = (
  statusType: string,
  studyLength: number,
  daysCompleted: number,
  progressColor?: string,
  size?: number,
  progressWidth?: number,
  isCompletedStudy?: boolean
) => {
  switch (statusType) {
    case 'progress':
      return (
        <>
          <CircularProgress
            percent={percentageCompleted(studyLength, daysCompleted)}
            size={size ? size : 94}
            color={
              progressColor ? progressColor : Colors.progressBackgroundColor
            }
            width={progressWidth}
          />
        </>
      );

    case 'completed':
      return (
        <>
          <CircularProgress
            percent={
              isCompletedStudy
                ? 100
                : percentageCompleted(studyLength, daysCompleted)
            }
            size={size ? size : 94}
            color={'#CDB9F5'}
            width={progressWidth}
          />
        </>
      );
    case 'withdrawn':
      return (
        <>
          <StyledStatusText color='medium'>Study</StyledStatusText>
          <StyledStatusText color='medium' testID={elementIDs.withdrawnLabelID}>
            Withdrawn
          </StyledStatusText>
        </>
      );
    case 'paused':
      return (
        <>
          <StyledStatusText color='medium'>Study</StyledStatusText>
          <StyledStatusText color='medium'>Paused</StyledStatusText>
        </>
      );
    default:
      return (
        <>
          <StyledStatusText color='medium'>Not</StyledStatusText>
          <StyledStatusText color='medium'>Started</StyledStatusText>
        </>
      );
  }
};

// Screen Component ------------------------------------------------------------

const StudyDynamicCircle: React.FC<StudyDynamicCircleProps> = ({
  statusType,
  startDate,
  studyLength,
  textSize,
  textColor,
  progressColor,
  progressWidth,
  size,
  display = 'daysLeft',
  isCompletedStudy
}) => {
  const hasProgressBar = ['progress', 'completed'].includes(statusType);
  // don't use the calculateStudyEndDate since we want the day after last day
  const endDate = startDate ? addDays(startDate, studyLength) : undefined;
  const daysCompleted =
    endDate && startDate
      ? differenceInDays(
          isBefore(endDate, new Date()) ? endDate : new Date(),
          startDate
        )
      : 0;
  const days =
    display === 'daysLeft' ? studyLength - daysCompleted : daysCompleted;
  return (
    <>
      {circleCustomView(
        statusType,
        studyLength,
        daysCompleted,
        progressColor,
        size,
        progressWidth,
        isCompletedStudy
      )}
      {hasProgressBar && (
        <StyledViewContainer>
          {
            <StyledStatusPercentText
              textSize={textSize}
              color={textColor ? textColor : 'medium'}
            >
              {!isCompletedStudy
                ? display === 'daysLeft'
                  ? days > 0
                    ? days
                    : percentageCompleted(studyLength, daysCompleted) + '%'
                  : days !== studyLength
                    ? days
                    : percentageCompleted(studyLength, daysCompleted) + '%'
                : '100%'}
            </StyledStatusPercentText>
          }
          <StyledStatusText
            textSize={textSize}
            color={textColor ? textColor : 'medium'}
          >
            {!isCompletedStudy
              ? display === 'daysLeft'
                ? days > 0
                  ? `Day${days > 1 ? 's' : ''} Left`
                  : 'Complete'
                : days !== studyLength
                  ? `Day${days > 1 || days === 0 ? 's' : ''} Logged`
                  : 'Complete'
              : 'Complete'}
          </StyledStatusText>
        </StyledViewContainer>
      )}
    </>
  );
};

export default StudyDynamicCircle;
